import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { setStyle, extension, command, invariant, ErrorConstant, NodeExtension, ExtensionTag, isElementDomNode, omitExtraAttributes, getTextSelection } from '@remirror/core';
import { insertPoint } from '@remirror/pm/transform';
import { ExtensionImageTheme } from '@remirror/theme';
import { ResizableNodeView, ResizableRatioType } from 'prosemirror-resizable-view';

/**
 * ResizableImageView is a NodeView for image. You can resize the image by
 * dragging the handle over the image.
 */
class ResizableImageView extends ResizableNodeView {
  constructor(node, view, getPos) {
    super({
      node,
      view,
      getPos,
      aspectRatio: ResizableRatioType.Fixed
    });
  }

  createElement(_ref) {
    var node = _ref.node;
    var inner = document.createElement('img');
    inner.setAttribute('src', node.attrs.src);
    setStyle(inner, {
      width: '100%',
      minWidth: '50px',
      objectFit: 'contain' // maintain image's aspect ratio

    });
    return inner;
  }

}

var _dec, _dec2, _dec3, _class, _class2;
var ImageExtension = (_dec = extension({
  defaultOptions: {
    createPlaceholder,
    updatePlaceholder: () => {},
    destroyPlaceholder: () => {},
    uploadHandler,
    enableResizing: false
  }
}), _dec2 = command(), _dec3 = command(), _dec(_class = (_class2 = class ImageExtension extends NodeExtension {
  get name() {
    return 'image';
  }

  createTags() {
    return [ExtensionTag.InlineNode, ExtensionTag.Media];
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({
      inline: true,
      draggable: true,
      selectable: false
    }, override), {}, {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        alt: {
          default: ''
        },
        crop: {
          default: null
        },
        height: {
          default: null
        },
        width: {
          default: null
        },
        rotate: {
          default: null
        },
        src: {
          default: null
        },
        title: {
          default: ''
        },
        fileName: {
          default: null
        },
        resizable: {
          default: false
        }
      }),
      parseDOM: [{
        tag: 'img[src]',
        getAttrs: element => isElementDomNode(element) ? getImageAttributes({
          element,
          parse: extra.parse
        }) : {}
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => {
        var attrs = omitExtraAttributes(node.attrs, extra);
        return ['img', _objectSpread(_objectSpread({}, extra.dom(node)), attrs)];
      }
    });
  }

  insertImage(attributes, selection) {
    return _ref => {
      var tr = _ref.tr,
          dispatch = _ref.dispatch;

      var _getTextSelection = getTextSelection(selection !== null && selection !== void 0 ? selection : tr.selection, tr.doc),
          from = _getTextSelection.from,
          to = _getTextSelection.to;

      var node = this.type.create(attributes);
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.replaceRangeWith(from, to, node));
      return true;
    };
  }
  /**
   * Insert an image once the provide promise resolves.
   */


  uploadImage(value, onElement) {
    var _this$options = this.options,
        updatePlaceholder = _this$options.updatePlaceholder,
        destroyPlaceholder = _this$options.destroyPlaceholder,
        createPlaceholder = _this$options.createPlaceholder;
    return props => {
      var tr = props.tr; // This is update in the validate hook

      var pos = tr.selection.from;
      return this.store.createPlaceholderCommand({
        promise: value,
        placeholder: {
          type: 'widget',

          get pos() {
            return pos;
          },

          createElement: (view, pos) => {
            var element = createPlaceholder(view, pos);
            onElement === null || onElement === void 0 ? void 0 : onElement(element);
            return element;
          },
          onUpdate: (view, pos, element, data) => {
            updatePlaceholder(view, pos, element, data);
          },
          onDestroy: (view, element) => {
            destroyPlaceholder(view, element);
          }
        },
        onSuccess: (value, range, commandProps) => {
          return this.insertImage(value, range)(commandProps);
        }
      }).validate(_ref2 => {
        var tr = _ref2.tr,
            dispatch = _ref2.dispatch;
        var insertPos = insertPoint(tr.doc, pos, this.type);

        if (insertPos == null) {
          return false;
        }

        pos = insertPos;

        if (!tr.selection.empty) {
          dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.deleteSelection());
        }

        return true;
      }, 'unshift').generateCommand()(props);
    };
  }

  fileUploadFileHandler(files) {
    var _this$store = this.store,
        commands = _this$store.commands,
        chain = _this$store.chain;
    var filesWithProgress = files.map((file, index) => ({
      file,
      progress: function (_progress) {
        function progress(_x) {
          return _progress.apply(this, arguments);
        }

        progress.toString = function () {
          return _progress.toString();
        };

        return progress;
      }(progress => {
        commands.updatePlaceholder(uploads[index], progress);
      })
    }));
    var uploads = this.options.uploadHandler(filesWithProgress);

    var _iterator = _createForOfIteratorHelper(uploads),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var upload = _step.value;
        chain.uploadImage(upload);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    chain.run();
    return true;
  }

  createPasteRules() {
    return [{
      type: 'file',
      regexp: /image/i,
      fileHandler: _ref3 => {
        var files = _ref3.files;
        return this.fileUploadFileHandler(files);
      }
    }];
  }

  createNodeViews() {
    if (this.options.enableResizing) {
      return (node, view, getPos) => {
        return new ResizableImageView(node, view, getPos);
      };
    }

    return {};
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "insertImage", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "insertImage"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "uploadImage", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "uploadImage"), _class2.prototype)), _class2)) || _class);

/**
 * The set of valid image files.
 */
var IMAGE_FILE_TYPES = new Set(['image/jpeg', 'image/gif', 'image/png', 'image/jpg', 'image/svg', 'image/webp']);
/**
 * True when the provided file is an image file.
 */

function isImageFileType(file) {
  return IMAGE_FILE_TYPES.has(file.type);
}
/**
 * Get the width and the height of the image.
 */

function getDimensions(element) {
  var _ref4, _width, _ref5, _height;

  var _element$style = element.style,
      width = _element$style.width,
      height = _element$style.height;
  width = (_ref4 = (_width = width) !== null && _width !== void 0 ? _width : element.getAttribute('width')) !== null && _ref4 !== void 0 ? _ref4 : '';
  height = (_ref5 = (_height = height) !== null && _height !== void 0 ? _height : element.getAttribute('height')) !== null && _ref5 !== void 0 ? _ref5 : '';
  return {
    width,
    height
  };
}
/**
 * Retrieve attributes from the dom for the image extension.
 */


function getImageAttributes(_ref6) {
  var _element$getAttribute, _element$getAttribute2, _element$getAttribute3, _element$getAttribute4;

  var element = _ref6.element,
      parse = _ref6.parse;

  var _getDimensions = getDimensions(element),
      width = _getDimensions.width,
      height = _getDimensions.height;

  return _objectSpread(_objectSpread({}, parse(element)), {}, {
    alt: (_element$getAttribute = element.getAttribute('alt')) !== null && _element$getAttribute !== void 0 ? _element$getAttribute : '',
    height: Number.parseInt(height || '0', 10) || null,
    src: (_element$getAttribute2 = element.getAttribute('src')) !== null && _element$getAttribute2 !== void 0 ? _element$getAttribute2 : null,
    title: (_element$getAttribute3 = element.getAttribute('title')) !== null && _element$getAttribute3 !== void 0 ? _element$getAttribute3 : '',
    width: Number.parseInt(width || '0', 10) || null,
    fileName: (_element$getAttribute4 = element.getAttribute('data-file-name')) !== null && _element$getAttribute4 !== void 0 ? _element$getAttribute4 : null
  });
}

function createPlaceholder(_, __) {
  var element = document.createElement('div');
  element.classList.add(ExtensionImageTheme.IMAGE_LOADER);
  return element;
}
/**
 * The default handler converts the files into their `base64` representations
 * and adds the attributes before inserting them into the editor.
 */


function uploadHandler(files) {
  !(files.length > 0) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.EXTENSION,
    message: 'The upload handler was applied for the image extension without any valid files'
  }) : invariant(false) : void 0;
  var completed = 0;
  var promises = [];

  var _iterator2 = _createForOfIteratorHelper(files),
      _step2;

  try {
    var _loop = function _loop() {
      var _step2$value = _step2.value,
          file = _step2$value.file,
          progress = _step2$value.progress;
      promises.push(() => new Promise(resolve => {
        var reader = new FileReader();
        reader.addEventListener('load', readerEvent => {
          var _readerEvent$target;

          completed += 1;
          progress(completed / files.length);
          resolve({
            src: (_readerEvent$target = readerEvent.target) === null || _readerEvent$target === void 0 ? void 0 : _readerEvent$target.result,
            fileName: file.name
          });
        }, {
          once: true
        });
        reader.readAsDataURL(file);
      }));
    };

    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      _loop();
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  return promises;
}

export { ImageExtension, ResizableImageView, isImageFileType };
